/**
 * Life Graph Process
 * 
 * Graph-based conversation context system that visualizes user's life domains/goals
 * and allows clicking nodes to shift conversation context.
 * 
 * Uses Graph App's shared rendering components.
 */

import type { ProcessContext } from '../../systems/process.js';
import { ProcessPriority } from '../../systems/process.js';
import { BaseProcess } from '../../systems/base-process.js';
import { BaseViewHTML } from '../../systems/base-view-html.js';
import { registerProcess } from '../../systems/registry.js';
import { bus } from '../../core/events.js';
import { log } from '../../services/logging/index.js';
import { GraphEngine } from '../graph-app/graph-engine.js';
import { renderGraphView } from '../graph-app/graph-renderer.js';
import type { GraphNode, GraphEdge } from '../graph-app/types.js';
import { nanoid } from 'nanoid';
import {
  designTokens,
  createHeading,
  createContainer
} from '../../core/design-system.js';

interface LifeGraphState {
  selectedDomain: string | null;
  highlightedDomains: Set<string>;
  domains: GraphNode[];
}

export class LifeGraphProcess extends BaseProcess {
  kind: string = 'life-graph';

  metadata = {
    priority: ProcessPriority.NORMAL,
    category: 'productivity' as const,
    canStop: true,
    preserveState: true,
    timeSensitive: false
  };

  private graphEngine: GraphEngine = new GraphEngine();
  private state: LifeGraphState = {
    selectedDomain: null,
    highlightedDomains: new Set(),
    domains: []
  };

  private lifeGraphView: LifeGraphView | null = null;

  constructor(id: string) {
    super(id);
    this.initializeLifeDomains();
  }

  /**
   * Initialize with sample life domains (will be replaced with real data from Graphiti/D1)
   */
  private initializeLifeDomains(): void {
    const sampleDomains = [
      { id: 'career', label: 'Career', domain: 'career', description: 'Professional goals and decisions' },
      { id: 'fitness', label: 'Fitness', domain: 'fitness', description: 'Health and wellness goals' },
      { id: 'finance', label: 'Finance', domain: 'finance', description: 'Financial planning and goals' },
      { id: 'relationships', label: 'Relationships', domain: 'relationships', description: 'Personal connections' },
      { id: 'health', label: 'Health', domain: 'health', description: 'Physical and mental health' },
      { id: 'learning', label: 'Learning', domain: 'learning', description: 'Education and skill development' }
    ];

    sampleDomains.forEach(domain => {
      const node: GraphNode = {
        id: domain.id,
        type: 'content',
        data: {
          domain: domain.domain,
          description: domain.description
        },
        metadata: {
          label: domain.label
        }
      };
      this.state.domains.push(node);
      this.graphEngine.addNode(node);
    });

    // Create relationship edges between related domains
    this.createDomainRelationships();
  }

  /**
   * Create relationship edges between domains
   * In the real implementation, this would be based on:
   * - Conversation frequency
   * - Decision relationships
   * - User-defined connections
   */
  private createDomainRelationships(): void {
    // Career and Finance are related
    this.graphEngine.addEdge({
      id: `edge-${nanoid()}`,
      from: 'career',
      to: 'finance',
      type: 'dependency'
    });

    // Fitness and Health are related
    this.graphEngine.addEdge({
      id: `edge-${nanoid()}`,
      from: 'fitness',
      to: 'health',
      type: 'dependency'
    });

    // Career and Learning are related
    this.graphEngine.addEdge({
      id: `edge-${nanoid()}`,
      from: 'career',
      to: 'learning',
      type: 'dependency'
    });

    // Finance and Health are related (healthcare costs)
    this.graphEngine.addEdge({
      id: `edge-${nanoid()}`,
      from: 'finance',
      to: 'health',
      type: 'influence'
    });
  }

  protected onStart(ctx: ProcessContext): void {
    log.debug(`[LifeGraphProcess] Starting life graph process ${this.id}`);

    // Listen for domain selection events
    bus.on('life-graph.domain.selected', (payload: unknown) => {
      const data = payload as { domainId: string };
      this.selectDomain(data.domainId);
    });

    // Auto-show view on start
    this.showLifeGraphView(ctx);

    bus.emit('process.started', { processId: this.id, kind: this.kind });
  }

  private showLifeGraphView(ctx: ProcessContext): void {
    if (this.lifeGraphView) {
      return; // Already showing
    }

    const view = new LifeGraphView(
      this.id,
      `${this.id}-main`,
      this.state,
      this.graphEngine,
      this
    );
    const viewInstance = view.toViewInstance();
    this.lifeGraphView = view;

    ctx.registerView(viewInstance);
    this.views.set(viewInstance.id, viewInstance);
  }

  selectDomain(domainId: string): void {
    if (this.state.selectedDomain === domainId) {
      // Deselect if clicking the same domain
      this.state.selectedDomain = null;
    } else {
      this.state.selectedDomain = domainId;
    }

    // Update highlighted domains (related domains)
    this.updateHighlightedDomains();

    if (this.lifeGraphView) {
      this.lifeGraphView.updateState(this.state);
    }

    // Emit context change event for conversation context manager
    bus.emit('conversation.context.switch', {
      domain: this.state.selectedDomain,
      domainId: this.state.selectedDomain
    });

    log.debug(`[LifeGraphProcess] Domain selected: ${this.state.selectedDomain}`);
  }

  /**
   * Update highlighted domains based on selected domain
   */
  private updateHighlightedDomains(): void {
    this.state.highlightedDomains.clear();

    if (!this.state.selectedDomain) {
      return;
    }

    // Find related domains (connected via edges)
    const edges = this.graphEngine.getEdgesForNode(this.state.selectedDomain);
    edges.forEach(edge => {
      const relatedId = edge.from === this.state.selectedDomain ? edge.to : edge.from;
      if (relatedId !== this.state.selectedDomain) {
        this.state.highlightedDomains.add(relatedId);
      }
    });
  }

  getState(): LifeGraphState {
    return { ...this.state };
  }

  getGraphEngine(): GraphEngine {
    return this.graphEngine;
  }
}

/**
 * Life Graph View
 * 
 * Renders the life graph visualization using shared Graph App components.
 */
class LifeGraphView extends BaseViewHTML {
  constructor(
    processId: string,
    viewId: string,
    private state: LifeGraphState,
    private graphEngine: GraphEngine,
    private process: LifeGraphProcess
  ) {
    super(processId, viewId, {
      sizes: [3,0], // [1] = full+standard (default), [3] = full+double
      showDismissButton: true,
      resizable: true,
    });
  }

  protected getContentHTML(): string {
    const heading = createHeading(2, 'Life Graph');

    // Current context indicator
    const contextIndicator = this.state.selectedDomain
      ? `
        <div style="
          margin-bottom: ${designTokens.padding.md};
          padding: ${designTokens.padding.sm};
          background: rgba(102, 204, 255, 0.1);
          border: 1px solid rgba(102, 204, 255, 0.3);
          border-radius: ${designTokens.radius.sm};
          font-size: ${designTokens.fontSize.sm};
          color: ${designTokens.contentColor};
        ">
          <strong>Current Context:</strong> ${this.getDomainLabel(this.state.selectedDomain)}
        </div>
      `
      : `
        <div style="
          margin-bottom: ${designTokens.padding.md};
          padding: ${designTokens.padding.sm};
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid ${designTokens.borderColor};
          border-radius: ${designTokens.radius.sm};
          font-size: ${designTokens.fontSize.sm};
          color: ${designTokens.contentColorMuted};
        ">
          Click on a domain to shift conversation context
        </div>
      `;

    // Graph visualization
    const graphVisualization = this.renderGraph();

    return createContainer(
      heading +
      contextIndicator +
      `<div class="life-graph-visualization" style="
        margin-top: ${designTokens.padding.lg};
        min-height: 400px;
        border: 1px solid ${designTokens.borderColor};
        border-radius: ${designTokens.radius.md};
        padding: ${designTokens.padding.md};
        overflow: auto;
        transition: opacity 0.3s ease, transform 0.3s ease;
      ">
        ${graphVisualization}
      </div>`,
      { padding: 'lg', flexDirection: 'column' }
    );
  }

  /**
   * Render graph using shared renderer
   */
  private renderGraph(): string {
    const contentNodes = this.state.domains;

    // Set up click and hover handlers
    (window as any).__graphNodeClick = (nodeId: string) => {
      this.process.selectDomain(nodeId);
    };

    (window as any).__graphNodeHover = (nodeId: string | null) => {
      // Could add hover effects here
    };

    return renderGraphView(contentNodes, this.graphEngine, {
      width: 700,
      height: 400,
      selectedNodeId: this.state.selectedDomain,
      highlightedNodeIds: this.state.highlightedDomains,
      categoryColors: {
        career: '#66ccff',
        fitness: '#4ade80',
        finance: '#a78bfa',
        relationships: '#fbbf24',
        health: '#f87171',
        learning: '#60a5fa',
        default: '#94a3b8'
      }
    });
  }

  /**
   * Get domain label
   */
  private getDomainLabel(domainId: string | null): string {
    if (!domainId) return '';
    const domain = this.state.domains.find(d => d.id === domainId);
    return (domain?.metadata?.label as string) || domainId;
  }

  /**
   * Setup handlers to prevent window dragging when interacting with visualization
   */
  protected render(): void {
    super.render();

    if (!this.element) return;

    // Prevent window dragging when clicking inside visualization area
    const visualizationArea = this.element.querySelector('.life-graph-visualization');
    if (visualizationArea) {
      visualizationArea.addEventListener('mousedown', (e) => {
        e.stopPropagation();
      }, true);

      // Also prevent dragging on graph nodes
      const graphNodes = visualizationArea.querySelectorAll('.graph-node, svg');
      graphNodes.forEach(el => {
        el.addEventListener('mousedown', (e) => {
          e.stopPropagation();
        }, true);
      });
    }
  }

  updateState(newState: LifeGraphState): void {
    this.state = newState;
    this.markDirty();
  }
}

registerProcess('life-graph', (id: string) => new LifeGraphProcess(id));

